import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const FacilityChart = React.memo(({ facilityId, facilityChartData, facilityColor = '#5470c6' }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    
    // 查找对应设施的图表数据，用于获取 displayScore
    const facilityChart = facilityChartData.find(item => item.facilityId === facilityId);
    const displayScore = facilityChart?.overallScore || 0;
    
    // 初始化图表
    const initChart = () => {
        if (!chartRef.current) return;
        
        // 查找对应设施的图表数据
        const facilityChart = facilityChartData.find(item => item.facilityId === facilityId);
        if (!facilityChart || !facilityChart.chartData.length) return;
        
        // 清除之前的图表实例
        if (chartInstanceRef.current) {
            echarts.dispose(chartInstanceRef.current);
        }
        
        const myChart = echarts.init(chartRef.current);
        chartInstanceRef.current = myChart;
        
        // 创建名称到缩写的映射函数
        const getAbbreviation = (name) => {
            // 将名称按空格分割，取每个单词的首字母并转为大写
            return name.split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .join('');
        };
        
        // 创建名称映射表，用于 tooltip 显示全称
        const nameMap = {};
        const originalNames = facilityChart.chartData.map(item => item.name);
        const indicators = facilityChart.chartData.map((item) => {
            const abbrev = getAbbreviation(item.name);
            nameMap[abbrev] = item.name;
            return {
                name: abbrev, // 使用缩写作为显示名称
                max: 10 // 设置最大值为10
            };
        });
        
        const option = {
            backgroundColor: '#fff',
            grid: {
                left: '10%',
                right: '10%',
                top: '5%',
                bottom: '10%'
            },
            tooltip: {
                trigger: 'item',
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;', // 设置更高的z-index
                formatter: function(params) {
                    // 对于雷达图，params.value 是一个数组，包含所有指标的值
                    // params.seriesName 是系列名称
                    // 需要将 values 数组与 indicators 的 name 对应起来
                    if (Array.isArray(params.value)) {
                        // params.value 是值数组，需要与 indicators 对应
                        return params.value.map((value, index) => {
                            // 通过索引找到对应的指标名称
                            const indicatorName = indicators[index]?.name || '';
                            const fullName = nameMap[indicatorName] || originalNames[index] || indicatorName;
                            // 显示格式：全称(缩写): 值
                            return `${fullName}(${indicatorName}): ${value}`;
                        }).join('<br/>');
                    } else if (Array.isArray(params)) {
                        // 如果是参数数组
                        return params.map(param => {
                            const fullName = nameMap[param.name] || originalNames[param.dataIndex] || param.name;
                            // 显示格式：全称(缩写): 值
                            return `${fullName}(${param.name}): ${param.value}`;
                        }).join('<br/>');
                    } else {
                        // 单个数据点
                        const fullName = nameMap[params.name] || originalNames[params.dataIndex] || params.name;
                        // 显示格式：全称(缩写): 值
                        return `${fullName}(${params.name}): ${params.value}`;
                    }
                }
            },
            legend: {
                show: false
            },
            radar: {
                indicator: indicators,
                center: ['50%', '50%'],
                radius: '76%', // 调整半径以适应6个指标
                axisName: {
                    color: '#666',
                    fontSize: 11, // 稍微减小字体以适应更多指标
                    formatter: function(value) {
                        // 直接返回缩写，因为已经转换过了
                        return value;
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                }
            },
            series: [{
                type: 'radar',
                data: [{
                    value: facilityChart.chartData.map(item => item.value), // 使用当前值
                    name: facilityChart.facilityName || 'Facility Performance',
                    itemStyle: {
                        color: facilityColor // 直接使用传入的设施颜色
                    },
                    lineStyle: {
                        color: facilityColor // 直接使用传入的设施颜色
                    },
                    areaStyle: {
                        color: facilityColor, // 直接使用传入的设施颜色
                        opacity: 0.1
                    }
                }]
            }]
        };
        
        myChart.setOption(option);
        
        // 响应式
        const resizeHandler = () => {
            myChart.resize();
        };
        window.addEventListener('resize', resizeHandler);
        
        // 清理函数
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    };

    useEffect(() => {
        const cleanup = initChart();
        return cleanup;
    }, [facilityId, facilityChartData, facilityColor]);

    useEffect(() => {
        return () => {
            if (chartInstanceRef.current) {
                echarts.dispose(chartInstanceRef.current);
            }
        };
    }, []);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ 
                textAlign: 'center', 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#333',
                flexShrink: 0
            }}>
                Facility Score: {displayScore.toFixed(2)}
            </div>
            <div 
                ref={chartRef}
                style={{ width: '100%', flex: 1, minHeight: 0 }}
            />
        </div>
    );
}, (prevProps, nextProps) => {
    // 自定义比较函数，只有在这些属性真正改变时才重新渲染
    return (
        prevProps.facilityId === nextProps.facilityId &&
        prevProps.facilityColor === nextProps.facilityColor &&
        prevProps.facilityChartData === nextProps.facilityChartData
    );
});

// 设置 displayName 用于调试
FacilityChart.displayName = 'FacilityChart';

export default FacilityChart;
